import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import * as Papa from "papaparse";
import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import type {
    ICSVParseResults,
    ICSVPromptData,
    IClassifyPromptsResponse,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { getCharsetEncoding } from "app/UtilitiesAndConstants/UtilityFunctions/getCharsetEncoding";

import { ALLOWED_CSV_EXTENSIONS, ALLOWED_CSV_MIME_TYPES, CSV_COLUMN_ALIASES } from "../consts";

interface ICSVUploadHook {
    handleUpload: (files: File[]) => Promise<void>;
    isUploading: boolean;
}

interface IResolvedTopics {
    prompts: ICSVPromptData[];
    autoClassified: number;
    newTopicFallback: number;
}

const fetchService = DefaultFetchService.getInstance();
const getClassifyPromptsEndpoint = (campaignId: string): string =>
    `/api/aiTracker/campaign/${campaignId}/classify-prompts-topics`;

export const useCSVUpload = (
    onUploadSuccess: (prompts: ICSVPromptData[]) => Promise<number>,
    campaignId?: string,
): ICSVUploadHook => {
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const dispatch = useDispatch();

    const { mutateAsync: classifyPromptsTopics } = useMutation({
        mutationFn: ({ campaignId: id, prompts }: { campaignId: string; prompts: ICSVPromptData[] }) =>
            fetchService.post<IClassifyPromptsResponse>(getClassifyPromptsEndpoint(id), { prompts }),
        onError: (error, variables) => {
            Sentry.errorTracker({
                message: "Error classifying prompts to topics",
                e: error,
                level: "error",
                params: {
                    extra: {
                        campaignId: variables.campaignId,
                        promptCount: variables.prompts.length,
                    },
                },
            });
        },
    });

    const triggerError = (trackingSubSubName: string, errorToastKey: string): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "error", `add prompts/csv upload failed/${trackingSubSubName}`);
        dispatch(showErrorToast(translate(errorToastKey)));
    };

    const parseCSVData = (results: Papa.ParseResult<Record<string, string>>): ICSVParseResults | null => {
        if (!results.data || !results.data.length) {
            triggerError("empty file", "llm.prompts.edit.prompts-table.csv.upload.error.empty");
            return null;
        }

        //identify columns
        const headers = results.meta.fields || [];
        const { prompt: promptsColNames, topic: topicsColNames } = CSV_COLUMN_ALIASES;
        const promptColumn = headers.find((header) => promptsColNames.includes(header.toLowerCase()));
        const topicColumn = headers.find((header) => topicsColNames.includes(header.toLowerCase()));

        if (!promptColumn) {
            triggerError("missing prompt column", "llm.prompts.edit.prompts-table.csv.upload.error.no-prompt-column");
            return null;
        }

        //extract data from all columns
        const extractedData: ICSVPromptData[] = results.data
            .map((row) => ({
                prompt: row[promptColumn]?.trim().toLowerCase(),
                topic: topicColumn ? row[topicColumn]?.trim() : undefined,
            }))
            .filter((item) => item.prompt && item.prompt.length);

        if (!extractedData.length) {
            triggerError("no valid prompts", "llm.prompts.edit.prompts-table.csv.upload.error.no-valid-prompts");
            return null;
        }

        const canonicalTopicByKey = new Map<string, string>();
        extractedData.forEach((item) => {
            if (item.topic && !canonicalTopicByKey.has(item.topic.toLowerCase())) {
                canonicalTopicByKey.set(item.topic.toLowerCase(), item.topic);
            }
        });
        const canonicalisedData: ICSVPromptData[] = extractedData.map((item) => ({
            ...item,
            topic: item.topic ? canonicalTopicByKey.get(item.topic.toLowerCase()) : undefined,
        }));

        //remove duplicate prompts
        const deduplicatedData = Array.from(new Map(canonicalisedData.map((item) => [item.prompt, item])).values());
        const skippedCount = canonicalisedData.length - deduplicatedData.length;

        return {
            data: deduplicatedData,
            skipped: skippedCount,
        };
    };

    const resolveTopicsViaClassification = async (prompts: ICSVPromptData[]): Promise<IResolvedTopics> => {
        const hasUnassignedTopics = prompts.some(({ topic }) => !topic);

        if (!campaignId || !hasUnassignedTopics) {
            return { prompts, autoClassified: 0, newTopicFallback: 0 };
        }

        try {
            const { results } = await classifyPromptsTopics({ campaignId, prompts });
            const suggestedTopicByPrompt = new Map(
                results.map(({ prompt, suggestedTopicName }) => [prompt, suggestedTopicName]),
            );

            const assignedPrompts: ICSVPromptData[] = [];
            const newTopicPrompts: ICSVPromptData[] = [];
            let newTopicFallback = 0;
            let autoClassified = 0;

            prompts.forEach((item) => {
                if (item.topic) {
                    assignedPrompts.push(item);
                    return;
                }

                const suggestedTopic = suggestedTopicByPrompt.get(item.prompt) ?? null;
                if (suggestedTopic) {
                    autoClassified++;
                    assignedPrompts.push({ ...item, topic: suggestedTopic });
                } else {
                    newTopicFallback++;
                    newTopicPrompts.push({ ...item, topic: undefined });
                }
            });

            return {
                prompts: [...assignedPrompts, ...newTopicPrompts],
                autoClassified,
                newTopicFallback,
            };
        } catch {
            return {
                prompts,
                autoClassified: 0,
                newTopicFallback: 0,
            };
        }
    };

    const handleUpload = async (files: File[]): Promise<void> => {
        if (!files.length) {
            return;
        }

        setIsUploading(true);

        //validate single file
        if (files.length > 1) {
            triggerError("multiple files", "llm.prompts.edit.prompts-table.csv.upload.error.multiple-files");
            setIsUploading(false);
            return;
        }

        //validate file type
        const uploadedFile = files[0];
        const fileExtension = uploadedFile.name.split(".").pop()?.toLowerCase() ?? "";
        if (
            !fileExtension ||
            (!ALLOWED_CSV_MIME_TYPES.includes(uploadedFile.type) && !ALLOWED_CSV_EXTENSIONS.includes(fileExtension))
        ) {
            triggerError("invalid file type", "llm.prompts.edit.prompts-table.csv.upload.error.invalid-file-type");
            setIsUploading(false);
            return;
        }

        try {
            const encoding = await getCharsetEncoding(uploadedFile);
            Papa.parse(uploadedFile, {
                header: true,
                encoding,
                complete: (results: Papa.ParseResult<Record<string, string>>): void => {
                    void (async (): Promise<void> => {
                        try {
                            const parseRes = parseCSVData(results);
                            const parsedPrompts = parseRes?.data;
                            const parsedPromptsCount = parsedPrompts?.length ?? 0;

                            if (parseRes) {
                                if (parsedPromptsCount) {
                                    const {
                                        prompts: resolvedPrompts,
                                        autoClassified,
                                        newTopicFallback,
                                    } = await resolveTopicsViaClassification(parseRes.data);

                                    const addedPrompts = await onUploadSuccess(resolvedPrompts);
                                    const duplicatesInFile = parseRes.skipped;
                                    const duplicatesInData = parsedPromptsCount - addedPrompts;
                                    const skippedPrompts = duplicatesInFile + duplicatesInData;

                                    allTrackers.dispatchTrackEvent(
                                        "Edit Campaign",
                                        "upload",
                                        `add prompts/success/${addedPrompts}`,
                                    );

                                    if (autoClassified > 0) {
                                        allTrackers.dispatchTrackEvent(
                                            "Edit Campaign",
                                            "upload",
                                            `add prompts/csv auto classified/${autoClassified}`,
                                            { newTopicFallback },
                                        );

                                        dispatch(
                                            showSuccessToast(
                                                translate(
                                                    "llm.prompts.edit.prompts-table.csv.upload.success.auto-classified",
                                                    {
                                                        promptCount: addedPrompts,
                                                        classifiedCount: autoClassified,
                                                        newTopicCount: newTopicFallback,
                                                    },
                                                ),
                                            ),
                                        );
                                    } else {
                                        dispatch(
                                            showSuccessToast(
                                                translate("llm.prompts.edit.prompts-table.csv.upload.success", {
                                                    promptCount: addedPrompts,
                                                }),
                                            ),
                                        );
                                    }

                                    if (skippedPrompts > 0) {
                                        dispatch(
                                            showSuccessToast(
                                                translate("llm.prompts.edit.prompts-table.csv.upload.success.skipped", {
                                                    promptCount: skippedPrompts,
                                                }),
                                            ),
                                        );
                                    }
                                } else {
                                    triggerError(
                                        "no valid prompts",
                                        "llm.prompts.edit.prompts-table.csv.upload.error.no-valid-prompts",
                                    );
                                }
                            }
                        } catch (error) {
                            triggerError(
                                "data processing failure",
                                "llm.prompts.edit.prompts-table.csv.upload.error.parsing",
                            );
                            Sentry.errorTracker({
                                message: "Error processing CSV file",
                                e: error,
                                level: "error",
                            });
                        } finally {
                            setIsUploading(false);
                        }
                    })();
                },
                error: (error) => {
                    triggerError("parsing failure", "llm.prompts.edit.prompts-table.csv.upload.error.parsing");
                    setIsUploading(false);
                    Sentry.errorTracker({
                        message: "Error parsing CSV file",
                        e: error,
                        level: "error",
                    });
                },
            });
        } catch (error) {
            triggerError("corrupted file", "llm.prompts.edit.prompts-table.csv.upload.error.parsing");
            setIsUploading(false);
            Sentry.errorTracker({
                message: "Error reading CSV file",
                e: error,
                level: "error",
            });
        }
    };

    return {
        handleUpload,
        isUploading,
    };
};
